import React from "react";
import { motion, useInView } from "framer-motion";
import { useReducedMotion } from "../lib/useReducedMotion";

/**
 * Fades + slides up children when scrolled into view.
 * Use stagger=true on parent to animate children one after another.
 */
export const Reveal = ({
    children,
    delay = 0,
    y = 16,
    once = true,
    stagger = false,
    className = "",
    "data-testid": testId,
    as: Component = "div",
}) => {
    const reduced = useReducedMotion();
    const ref = React.useRef(null);
    const inView = useInView(ref, { once, margin: "-10% 0px -10% 0px" });

    if (reduced) {
        return (
            <Component className={className} data-testid={testId}>
                {children}
            </Component>
        );
    }

    const MotionTag = motion[Component] || motion.div;

    if (stagger) {
        return (
            <MotionTag
                ref={ref}
                className={className}
                data-testid={testId}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.08, delayChildren: delay } },
                }}
            >
                {children}
            </MotionTag>
        );
    }

    return (
        <MotionTag
            ref={ref}
            className={className}
            data-testid={testId}
            initial={{ opacity: 0, y }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
            transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </MotionTag>
    );
};

export const RevealItem = ({ children, y = 16, className = "", as = "div" }) => {
    const reduced = useReducedMotion();
    const MotionTag = motion[as] || motion.div;
    if (reduced) return <div className={className}>{children}</div>;
    return (
        <MotionTag
            className={className}
            variants={{
                hidden: { opacity: 0, y },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
            }}
        >
            {children}
        </MotionTag>
    );
};

export default Reveal;
